'use client';

import { Link } from '@/app/[lang]/(main)/components/shared/PrivateLink/ServerLink';
import { useTranslations } from 'next-intl';
import { Suspense } from 'react';
import clsx from 'clsx';
import { IconBrandFacebook, IconBrandTelegram } from '@tabler/icons-react';
import { usePathname } from '@/navigation';
import { SupportLink } from './SupportLink';
import { QrCodeMultiPlatform } from './QrCodeMultiPlatform';
import { MobileAppStoreLinks } from './MobileAppStoreLinks';
import { runtimeConfig } from '@/config/runtime-config';
import { isDoskaIsrael } from '@/config/brand';

const isTGMiniApp = runtimeConfig.domain.toLowerCase().startsWith('tg');
const telegramBotUsername = process.env.TELEGRAM_BOT_USERNAME;
const telegramBotUrl = telegramBotUsername ? `https://t.me/${telegramBotUsername}` : null;

const hideFooterOnMobile = (pathname: string) =>
  pathname.startsWith('/product/create') || pathname.startsWith('/product/edit/');

export function Footer() {
  const pathname = usePathname();
  const t = useTranslations('controls');
  const downloadLabel = t('appPromotion.downloadIn');
  const iosLabel = t('appPromotion.appStore');
  const androidLabel = t('appPromotion.googlePlay');
  const aboutUsLabel = t('footer.aboutUs');
  const faqLabel = t('footer.faq');

  const reviewsLabel = t('footer.reviews');
  return (
    <footer
      className={clsx(
        'flex-col lg:pt-[40px] pt-[30px] lg:mt-[100px] md:mt-[40px] mt-[28px] lg:px-0 pb-[31px] px-[20px] lg:items-center justify-between bg-bg2-day',
        hideFooterOnMobile(pathname) ? 'hidden lg:flex' : 'flex',
      )}
    >
      <div className='flex flex-col md:flex-row justify-between lg:w-[1280px] w-full lg:px-[40px] m-auto'>
        <aside className='flex flex-col justify-between lg:max-h-[166px]'>
          <div className='flex flex-col gap-[12px] lg:gap-[8px]'>
            <div className='flex lg:flex-row lg:flex-wrap flex-col gap-x-[20px] gap-y-[20px] lg:gap-y-[8px] text-[16px] text-primary'>
              <Suspense
                fallback={
                  <Link href='/' className='text-text-primary-day transition-colors hover:text-primary-green-day'>
                    {t('footer.support')}
                  </Link>
                }
              >
                <SupportLink />
              </Suspense>

              <Link href='/agreement' className='text-text-primary-day transition-colors hover:text-primary-green-day'>
                {t('footer.licenseAgreement')}
              </Link>

              <Link href='/privacy' className='text-text-primary-day transition-colors hover:text-primary-green-day'>
                {t('footer.policy')}
              </Link>

              <Link href='/about-us' className='text-text-primary-day transition-colors hover:text-primary-green-day'>
                {aboutUsLabel}
              </Link>

              <Link href='/faq' className='text-text-primary-day transition-colors hover:text-primary-green-day'>
                {faqLabel}
              </Link>

              <Link
                href='/recommend-specialist'
                className='text-text-primary-day transition-colors hover:text-primary-green-day'
              >
                {t('footer.recommendSpecialist')}
              </Link>

              <Link
                href='/reviews'
                target={isTGMiniApp ? '_self' : '_blank'}
                rel={isTGMiniApp ? '' : 'nofollow noopener'}
                className='text-text-primary-day transition-colors hover:text-primary-green-day'
                title={reviewsLabel}
              >
                {reviewsLabel}
              </Link>

              {isDoskaIsrael && (
                <a
                  href='https://mezia.co.il'
                  target={isTGMiniApp ? '_self' : '_blank'}
                  rel='noopener'
                  lang='he'
                  dir='rtl'
                  className='text-text-primary-day transition-colors hover:text-primary-green-day'
                >
                  {'לוח מודעות "מציאה"'}
                </a>
              )}
            </div>

            <div className='-ms-[8px] flex flex-row gap-[4px]'>
              {!runtimeConfig.isTelegramMiniApp && telegramBotUrl && (
                <Link className='w-[40px] h-[40px] flex items-center justify-center' href={telegramBotUrl}>
                  <IconBrandTelegram size={24} stroke={1.25} className='text-text-primary-day' aria-hidden />
                </Link>
              )}
              <Link
                className='w-[40px] h-[40px] flex items-center justify-center'
                href='https://www.facebook.com/doskatv '
              >
                <IconBrandFacebook size={24} stroke={1.25} className='text-text-primary-day' aria-hidden />
              </Link>
            </div>
          </div>
        </aside>

        {!runtimeConfig.isTelegramMiniApp && (
          <aside className='flex flex-col gap-[38px] md:mt-0 mt-[20px] lg:max-h-[166px]'>
            <div className='flex gap-[42px]'>
              <QrCodeMultiPlatform />
              <div className='flex flex-col lg:gap-[14.5px] md:gap-[30px]'>
                <div className='hidden md:block w-[220px] text-[18px] text-text-primary-day font-semibold leading-[normal] tracking-[-0.18px] '>
                  {t('footer.mobileAppConvenient')}
                </div>
                <MobileAppStoreLinks downloadLabel={downloadLabel} iosLabel={iosLabel} androidLabel={androidLabel} />
              </div>
            </div>
            <div className='lg:block hidden h-[20px]'></div>
          </aside>
        )}
      </div>
    </footer>
  );
}
